import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AppShell, { PageContainer } from "@/components/AppShell";
import { supabase } from "@/lib/supabaseClient";
import { useSupabaseSession } from "@/lib/SupabaseSessionContext";
import { usePresence } from "@/lib/PresenceContext";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Check, X, Search, MessageSquare, UserMinus, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PresenceDot from "@/components/PresenceDot";
import Avatar from "@/components/Avatar";

export default function FriendsPage() {
  const { ready, supabaseUserId } = useSupabaseSession();
  const { isOnline } = usePresence();
  const { lang } = useT();
  const isDE = (lang || "de").startsWith("de");

  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [tab, setTab] = useState("friends");

  const reload = useCallback(async () => {
    if (!ready || !supabase || !supabaseUserId) return;
    try {
      const { data } = await supabase
        .from("friendships")
        .select("id, requester_id, addressee_id, status, created_at")
        .or(`requester_id.eq.${supabaseUserId},addressee_id.eq.${supabaseUserId}`);
      const rows = data || [];
      const ids = new Set();
      rows.forEach((r) => { ids.add(r.requester_id); ids.add(r.addressee_id); });
      ids.delete(supabaseUserId);
      let profMap = {};
      if (ids.size) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, username, avatar_url, country, is_premium")
          .in("user_id", [...ids]);
        (profs || []).forEach((p) => { profMap[p.user_id] = p; });
      }
      const enrich = (r) => {
        const otherId = r.requester_id === supabaseUserId ? r.addressee_id : r.requester_id;
        return { ...r, other_id: otherId, other: profMap[otherId] || { username: "user", user_id: otherId } };
      };
      setFriends(rows.filter((r) => r.status === "accepted").map(enrich));
      setIncoming(rows.filter((r) => r.status === "pending" && r.addressee_id === supabaseUserId).map(enrich));
      setOutgoing(rows.filter((r) => r.status === "pending" && r.requester_id === supabaseUserId).map(enrich));
    } catch (_e) { /* noop */ }
  }, [ready, supabaseUserId]);

  useEffect(() => { reload(); }, [reload]);

  // Realtime
  useEffect(() => {
    if (!ready || !supabase || !supabaseUserId) return;
    const ch = supabase.channel(`friends:${supabaseUserId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "friendships",
          filter: `requester_id=eq.${supabaseUserId}` },
        () => reload(),
      )
      .on("postgres_changes",
        { event: "*", schema: "public", table: "friendships",
          filter: `addressee_id=eq.${supabaseUserId}` },
        () => reload(),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [ready, supabaseUserId, reload]);

  const accept = async (row) => {
    setIncoming((l) => l.filter((x) => x.id !== row.id));
    try {
      await supabase.from("friendships").update({ status: "accepted" }).eq("id", row.id);
      toast.success(isDE ? "Freund hinzugefügt" : "Friend added");
      // ping the original requester
      api.post("/friends/notify", { addressee_user_id: row.requester_id, kind: "request_accepted" }).catch(() => {});
    } catch (_e) {
      toast.error(isDE ? "Aktion fehlgeschlagen" : "Action failed");
      reload();
    }
  };
  const decline = async (row) => {
    setIncoming((l) => l.filter((x) => x.id !== row.id));
    try {
      await supabase.from("friendships").delete().eq("id", row.id);
    } catch (_e) { reload(); }
  };
  const cancel = async (row) => {
    setOutgoing((l) => l.filter((x) => x.id !== row.id));
    try {
      await supabase.from("friendships").delete().eq("id", row.id);
    } catch (_e) { reload(); }
  };
  const remove = async (row) => {
    setFriends((l) => l.filter((x) => x.id !== row.id));
    try {
      await supabase.from("friendships").delete().eq("id", row.id);
      toast.message(isDE ? "Entfernt" : "Removed");
    } catch (_e) { reload(); }
  };
  const startDM = async (otherId) => {
    try {
      const { data, error } = await supabase.rpc("get_or_create_dm", { target_user: otherId });
      if (error) throw error;
      window.location.href = `/app/chat/${data}`;
    } catch (_e) {
      toast.error(isDE ? "Chat-Öffnen fehlgeschlagen" : "Couldn't open chat");
    }
  };

  if (!ready) {
    return (
      <AppShell>
        <PageContainer>
          <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-10 text-center text-zinc-400">
            {isDE ? "Verbindung wird hergestellt…" : "Connecting…"}
          </div>
        </PageContainer>
      </AppShell>
    );
  }

  const counts = { friends: friends.length, incoming: incoming.length, outgoing: outgoing.length };

  return (
    <AppShell>
      <PageContainer className="!max-w-4xl">
        <header className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h1 className="font-display text-2xl sm:text-3xl font-bold inline-flex items-center gap-2" data-testid="friends-title">
            <Users size={20} className="text-neon" /> {isDE ? "Freunde" : "Friends"}
          </h1>
          <Button onClick={() => setSearchOpen(true)} className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold" data-testid="friends-search-cta">
            <Search size={14} className="mr-2" /> {isDE ? "Freund suchen" : "Find a friend"}
          </Button>
        </header>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
          {[
            { k: "friends", de: "Freunde", en: "Friends", n: counts.friends },
            { k: "incoming", de: "Eingehend", en: "Incoming", n: counts.incoming },
            { k: "outgoing", de: "Ausgehend", en: "Outgoing", n: counts.outgoing },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`friends-tab-${t.k}`}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors inline-flex items-center gap-1.5 ${
                tab === t.k ? "bg-neon/15 text-neon border border-neon/40" : "bg-white/5 text-zinc-300 hover:bg-white/10 border border-white/5"
              }`}
            >
              {isDE ? t.de : t.en}
              {t.n > 0 && (
                <span className="min-w-[18px] h-4 px-1 rounded-full bg-rose-500/80 text-white text-[10px] font-bold grid place-items-center">{t.n}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "friends" && (
          <List
            items={friends}
            empty={isDE ? "Noch keine Freunde. Such jemanden!" : "No friends yet. Find someone!"}
            testid="friends-list"
            row={(r) => (
              <FriendCard
                row={r}
                live={isOnline(r.other_id)}
                primaryLabel={isDE ? "Nachricht" : "Message"}
                onPrimary={() => startDM(r.other_id)}
                secondaryIcon={UserMinus}
                onSecondary={() => remove(r)}
                testid={`friend-${r.other_id}`}
              />
            )}
          />
        )}
        {tab === "incoming" && (
          <List
            items={incoming}
            empty={isDE ? "Keine offenen Anfragen" : "No pending requests"}
            testid="friends-incoming-list"
            row={(r) => (
              <FriendCard
                row={r}
                live={isOnline(r.other_id)}
                primaryLabel={isDE ? "Annehmen" : "Accept"}
                primaryIcon={Check}
                onPrimary={() => accept(r)}
                secondaryIcon={X}
                onSecondary={() => decline(r)}
                testid={`friend-req-in-${r.other_id}`}
              />
            )}
          />
        )}
        {tab === "outgoing" && (
          <List
            items={outgoing}
            empty={isDE ? "Keine ausgehenden Anfragen" : "No outgoing requests"}
            testid="friends-outgoing-list"
            row={(r) => (
              <FriendCard
                row={r}
                live={isOnline(r.other_id)}
                primaryLabel={isDE ? "Abbrechen" : "Cancel"}
                primaryIcon={X}
                onPrimary={() => cancel(r)}
                testid={`friend-req-out-${r.other_id}`}
              />
            )}
          />
        )}

        {searchOpen && <FriendSearchModal onClose={() => setSearchOpen(false)} onSent={reload} isDE={isDE} myId={supabaseUserId} />}
      </PageContainer>
    </AppShell>
  );
}

function List({ items, empty, row, testid }) {
  if (!items.length) {
    return (
      <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-8 text-center text-sm text-zinc-500" data-testid={`${testid}-empty`}>
        {empty}
      </div>
    );
  }
  return (
    <ul className="rounded-2xl border border-white/5 bg-zinc-900/50 divide-y divide-white/5 overflow-hidden" data-testid={testid}>
      {items.map((it, i) => <li key={it.id || i}>{row(it)}</li>)}
    </ul>
  );
}

function FriendCard({ row, live, primaryLabel, primaryIcon: PIcon = MessageSquare, onPrimary, secondaryIcon: SIcon, onSecondary, testid }) {
  const p = row.other || {};
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors" data-testid={testid}>
      <div className="relative shrink-0">
        <Avatar src={p.avatar_url} name={p.username} size={44} />
        {live && <PresenceDot userId={row.other_id} size={12} className="absolute -bottom-0.5 -right-0.5" />}
      </div>
      <div className="flex-1 min-w-0">
        <Link to={`/u/${encodeURIComponent(p.username || "")}`} className="block font-semibold text-sm hover:text-neon transition-colors truncate">
          {p.username || "user"}
        </Link>
        <div className="flex items-center gap-2 mt-0.5">
          {p.is_premium && <Badge className="bg-amber-500/15 border-amber-500/30 text-amber-300 text-[10px] px-1.5 py-0">Premium</Badge>}
          {p.country && <span className="text-[11px] text-zinc-500">{p.country}</span>}
        </div>
      </div>
      <div className="flex gap-1.5 shrink-0">
        <Button size="sm" onClick={onPrimary} className="rounded-full bg-neon text-black hover:bg-neon-hover h-8 font-bold text-xs" data-testid={`${testid}-primary`}>
          <PIcon size={12} className="mr-1" /> {primaryLabel}
        </Button>
        {SIcon && (
          <Button size="sm" variant="ghost" onClick={onSecondary} className="text-zinc-400 hover:text-rose-400 h-8 w-8 p-0" data-testid={`${testid}-secondary`}>
            <SIcon size={14} />
          </Button>
        )}
      </div>
    </div>
  );
}

function FriendSearchModal({ onClose, onSent, isDE, myId }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sentSet, setSentSet] = useState(new Set());

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/users/search", { params: { q } });
        setResults(r.data?.items || []);
      } catch (_e) { setResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const send = async (target) => {
    setSentSet((s) => new Set(s).add(target.user_id));
    try {
      const { error } = await supabase.from("friendships").insert({
        requester_id: myId, addressee_id: target.user_id, status: "pending",
      });
      if (error && !String(error.message || "").toLowerCase().includes("duplicate")) throw error;
      toast.success(isDE ? "Anfrage gesendet" : "Request sent");
      api.post("/friends/notify", {
        addressee_user_id: target.user_id, kind: "friend_request",
      }).catch(() => {});
      onSent?.();
    } catch (_e) {
      setSentSet((s) => { const n = new Set(s); n.delete(target.user_id); return n; });
      toast.error(isDE ? "Konnte nicht gesendet werden" : "Could not send");
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-5" onClick={(e) => e.stopPropagation()} data-testid="friends-search-modal">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-base">{isDE ? "Freund suchen" : "Find a friend"}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-rose-400"><X size={16} /></button>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={isDE ? "Username eingeben…" : "Type a username…"}
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-zinc-900 border border-white/10 text-sm text-zinc-100 outline-none focus:border-neon/50"
            data-testid="friends-search-input"
          />
        </div>
        <div className="mt-3 max-h-72 overflow-y-auto -mx-1">
          {q.trim().length < 2 ? (
            <p className="text-xs text-zinc-500 px-3 py-4 text-center">{isDE ? "Mindestens 2 Zeichen" : "Type at least 2 characters"}</p>
          ) : searching ? (
            <p className="text-xs text-zinc-500 px-3 py-4 text-center">{isDE ? "Suche…" : "Searching…"}</p>
          ) : results.length === 0 ? (
            <p className="text-xs text-zinc-500 px-3 py-4 text-center">{isDE ? "Keine Treffer" : "No matches"}</p>
          ) : (
            <ul className="space-y-1">
              {results.map((u) => {
                const sent = sentSet.has(u.user_id);
                return (
                  <li key={u.user_id} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5">
                    <Avatar src={u.avatar_url} name={u.username} size={32} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{u.username}</p>
                      <p className="text-[10px] text-zinc-500">{u.country || ""}{u.is_online ? " · online" : ""}</p>
                    </div>
                    <Button size="sm" onClick={() => send(u)} disabled={sent} className={`rounded-full font-bold h-7 text-[11px] ${sent ? "bg-zinc-800 text-zinc-500" : "bg-neon text-black hover:bg-neon-hover"}`} data-testid={`friends-add-${u.user_id}`}>
                      {sent ? <Check size={12} /> : <><UserPlus size={12} className="mr-1" /> {isDE ? "Add" : "Add"}</>}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
